'use client';
import { useGreenhouse } from '@/contexts/GreenhouseProvider';
import { useSearchParams } from 'next/navigation';
import { getJobTeam } from '@/libs/greenhouse/greenhouse-functions';
import { GreenhouseCustomFieldId } from '@/libs/greenhouse/greenhouse';

const PAGE_SIZE = 20;

const getPaginationRange = (current: number, total: number) => {
  const delta = 2;
  const range = [];
  const rangeWithDots = [];
  let l;
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
      range.push(i);
    }
  }
  for (const i of range) {
    if (l) {
      if (i - l === 2) rangeWithDots.push(l + 1);
      else if (i - l !== 1) rangeWithDots.push('...');
    }
    rangeWithDots.push(i);
    l = i;
  }
  return rangeWithDots;
};

export const useGreenhouseSearch = () => {
  const { jobs } = useGreenhouse();
  const searchParams = useSearchParams();

  const keyword = searchParams.get('keyword')?.toLowerCase() || '';
  const selectedLocations = searchParams.getAll('location');
  const selectedTeams = searchParams.getAll('team');
  const isRemoteOnly = searchParams.get('remote') === 'true';

  const currentPage = Number(searchParams.get('page')) || 1;

  const allFilteredJobs = jobs.filter(job => {
    const matchesKeyword =
      !keyword || job.title.toLowerCase().includes(keyword) || job.content?.toLowerCase().includes(keyword);

    const jobLocations = job.location.name.split(';').map(loc => loc.trim());
    const matchesLocation = selectedLocations.length === 0 || jobLocations.some(loc => selectedLocations.includes(loc));

    const jobTeam = getJobTeam(job) ?? '';
    const matchesTeam = selectedTeams.length === 0 || selectedTeams.includes(jobTeam);

    const isRemoteJob = job.metadata?.find(m => m.id === GreenhouseCustomFieldId.OpenToRemote)?.value === true;
    const matchesRemote = !isRemoteOnly || isRemoteJob;

    return matchesKeyword && matchesLocation && matchesTeam && matchesRemote;
  });

  const filteredCount = allFilteredJobs.length;
  const totalPages = Math.ceil(filteredCount / PAGE_SIZE);
  const startIndex = Math.max(0, (currentPage - 1) * PAGE_SIZE);
  const paginatedJobs = allFilteredJobs.slice(startIndex, startIndex + PAGE_SIZE);

  const startRange = filteredCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const endRange = Math.min(currentPage * PAGE_SIZE, filteredCount);

  return {
    filteredJobs: paginatedJobs,
    filteredCount,
    paginationRange: getPaginationRange(currentPage, totalPages),
    range: { start: startRange, end: endRange },
    currentPage,
    totalPages,
  };
};
